import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  DSCardWrapper,
  DSButton,
} from "@components/design-system/molecules";
import { DSTabs } from "@components/design-system/organisms";
import {
  faBook,
  faEdit,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";

import Notes from "./note/Notes";
import RecentAttachments from "./summary/RecentAttachments";
import { Signalment, KeyHistory } from "./summary/Summary";
import Title from "./Title";
import { NOTE_FIELDS } from "../../graphql/fragments/note";
import {
  useAccount,
  useNoteCreation,
  useNoteMultiselectContext,
  useRecordingJobType,
} from "../../hooks";
import { alert } from "../common/Alert";
import { checkisAllowedAndDownloadEntirePatient } from "../common/clinical/NotePDF";
import Record from "../common/record/Record";
import { EditablePatientModal as EditPatientModal } from "../patients/modal/EditablePatientModal";

const GET_NOTES = gql`
  ${NOTE_FIELDS}
  query patientWithNotes($uuid: String, $last: Boolean) {
    patient(uuid: $uuid) {
      name
      lastNameOfOwner
      uuid
      species
      sex
      breed
      isIntact
      age
      weight
      color
      microchip
      ownerContactInfo
      emergencyContactInfo
      keyHistory
      keyMedications
      vaccinationStatus
      birthdayParts {
        dd
        mm
        yyyy
      }
      user {
        uuid
        firstName
        lastName
        name
        roleId
        account {
          uuid
          logoFileLink
          headerInfoPdf
        }
      }
      notes(last: $last) {
        ...NoteFields
        audioStorageLink
        keyHistory
        keyMedications
        vaccinationStatus
        patient {
          uuid
        }
        autoTemplates {
          uuid
          name
        }
        attachments {
          uuid
          fileName
          contentType
          fileLink
          createdAt
        }
        noteGroup {
          uuid
          name
        }
      }
    }
  }
`;

const Notebook = () => {
  const [isRecording, setIsRecording] = useState(false);
  const { patientUUID } = useParams();
  const { accountType } = useAccount();
  const { selectedJobType } = useRecordingJobType();
  const { resetSelectedNotesList } = useNoteMultiselectContext();
  const createNote = useNoteCreation({ variant: "patient" });
  const [shouldShow, setShouldShow] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("signalment");
  const { control, getValues, reset } = useForm();

  const {
    data,
    refetch,
    loading: getNotesLoading,
  } = useQuery(GET_NOTES, {
    variables: {
      uuid: patientUUID,
      last: false,
    },
    fetchPolicy: "network-only",
  });

  const isPageLoading = getNotesLoading;

  function hideModal() {
    setShouldShow(false);
  }

  async function createNewNote(
    audioStorageLink,
    storageObjectName,
    _jobType,
    _isSyncedRecording,
    _isNoteFromFileUpload,
    _recordingAs,
    _title,
    recordingConsentEventUuid,
  ) {
    const values = getValues();
    const result = await createNote({
      audioStorageLink,
      storageObjectName,
      jobType: selectedJobType?.value || "medical_record",
      title: values.title,
      patientUuid: patientUUID,
      recordingConsentEventUuid,
    });

    alert("success", "Audio file uploaded!");
    refetch();
    return result;
  }

  return (
    <div>
      <EditPatientModal
        shouldShow={shouldShow}
        hideModal={hideModal}
        isForPatientEdit={true}
        currentPatientInfo={data?.patient}
        refetch={refetch}
      />

      <div className="my-6 flex items-end">
        <div className="flex-1">
          <Title
            patientUUID={patientUUID}
            patient={data?.patient}
            dataTestId="pTitle"
          />
        </div>
        <div className="flex flex-col md:flex-row items-center gap-sm">
          <div id="editPatientButton">
            <DSButton
              icon={faEdit}
              variant="secondary"
              onClick={() => setShouldShow(true)}
              dataTestId="editPatientButton"
            >
              Edit Patient
            </DSButton>
          </div>
          <div id="exportNotebookButton">
            <DSButton
              disabled={isExporting}
              variant="secondary"
              icon={isExporting ? faSpinner : faBook}
              onClick={() => {
                checkisAllowedAndDownloadEntirePatient(
                  data?.patient?.user,
                  data?.patient,
                  setIsExporting,
                );
              }}
              dataTestId="exportNotebookButton"
            >
              Export Notebook
            </DSButton>
          </div>
        </div>
      </div>
      <div id="patientNotebookTabs">
        <DSTabs
          value={activeTab}
          onChange={(tabId) => {
            setActiveTab(tabId);
            resetSelectedNotesList();
          }}
          dataTestId="patientNotebookTabs"
        >
          <DSTabs.List>
            <DSTabs.Tab value="signalment">Signalment</DSTabs.Tab>
            <DSTabs.Tab value="keyHistory">Key History</DSTabs.Tab>
            <DSTabs.Tab value="attachments">Attachments</DSTabs.Tab>
          </DSTabs.List>
          <DSTabs.Panel value="signalment">
            <DSCardWrapper variant="default" id="patientSignalment">
              <Signalment
                patient={data?.patient}
                onOpenEdit={() => setShouldShow(true)}
              />
            </DSCardWrapper>
          </DSTabs.Panel>
          <DSTabs.Panel value="keyHistory">
            <DSCardWrapper variant="default" id="patientKeyHistory">
              <KeyHistory
                patient={data?.patient}
                patientUUID={patientUUID}
                onSaved={refetch}
              />
            </DSCardWrapper>
          </DSTabs.Panel>
          <DSTabs.Panel value="attachments">
            <DSCardWrapper variant="default" id="patientAttachments">
              <RecentAttachments
                isForPatientProfile={true}
                patientUUID={patientUUID}
              />
            </DSCardWrapper>
          </DSTabs.Panel>
        </DSTabs>
      </div>

      <div className="my-md">
        <DSCardWrapper variant="ghost">
          <Record
            createNewNote={createNewNote}
            id="recordButtonNotebook"
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            control={control}
            getValues={getValues}
            reset={reset}
            patientUUID={patientUUID}
            shouldShowRecordAs={false}
          />
        </DSCardWrapper>
      </div>

      <div className="mb-10" id="patientNotes">
        <Notes
          patientUUID={patientUUID}
          isPageLoading={isPageLoading}
          patient={data?.patient}
          data={data}
          refetch={refetch}
          isRecording={isRecording}
          accountType={accountType}
        />
      </div>
    </div>
  );
};

export default Notebook;
